import { setStorageItem } from 'app/adapters/storage';
import { clearSession, prepareLoginSession } from 'app/utils/sessions';
import {
  setFleetEmbedInStorage,
  setFleetEmbedLangInStorage,
} from 'app/utils/fleet-embed';
import {
  applyLanguageToClient,
  toApiLanguage,
} from 'app/utils/preferredLanguage';
import i18next from 'i18next';

function isFleetQueryEnabled(url: URL): boolean {
  return url.searchParams.get('isFleet') === 'true';
}

/**
 * Reads `?token=` from the URL (iframe / SSO embed), stores it for Bearer auth,
 * marks the user authenticated, and strips the token from the address bar.
 *
 * When `isFleet=true` is also present (Azuga Fleet ELOGS iframe), enables fleet
 * embed mode (hide header/sidebar). Other reseller embeds omit this flag and
 * keep the full portal chrome.
 *
 * When `lang=` is present (Fleet portal language), applies it after session clear
 * so the embedded UI matches Fleet (en | es | fr).
 *
 * Ends any prior server session before applying the new token so myUser reflects
 * the SSO identity (same class of bug as password login without prepareLoginSession).
 *
 * Must complete before React routes render so PrivateRoute allows deep links.
 *
 * @returns `true` if a token was applied.
 */
export async function bootstrapAuthTokenFromUrl(): Promise<boolean> {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');
  const isFleet = isFleetQueryEnabled(url);
  const langParam = url.searchParams.get('lang');

  if (!token && !isFleet) {
    return false;
  }

  let tokenApplied = false;

  if (token) {
    try {
      await prepareLoginSession();
    } catch {
      clearSession();
    }

    sessionStorage.setItem('authToken', token);
    // Keep localStorage in sync for call sites that read `token` directly.
    window.localStorage.setItem('token', token);
    setStorageItem('isAuthUser', 'true');
    url.searchParams.delete('token');
    tokenApplied = true;
  }

  // Apply after prepareLoginSession so the embed flag is not wiped.
  if (isFleet) {
    setFleetEmbedInStorage(true);
    url.searchParams.delete('isFleet');
  }

  // Apply after session clear so LanguageDetector / stale i18nextLng cannot win.
  if (langParam) {
    const languageCode = toApiLanguage(langParam);
    setFleetEmbedLangInStorage(languageCode);
    await applyLanguageToClient(languageCode, lng =>
      i18next.changeLanguage(lng),
    );
    url.searchParams.delete('lang');
  }

  const nextSearch = url.searchParams.toString();
  const nextUrl =
    url.pathname + (nextSearch ? `?${nextSearch}` : '') + url.hash;
  window.history.replaceState({}, '', nextUrl);

  return tokenApplied;
}
